const BASE = import.meta.env.VITE_API_BASE ?? '/api';

/**
 * Environnement réel tel que rapporté par le serveur (/sante) : 'production' | 'sandbox'.
 * Source de vérité unique — évite que le front (compilé) diverge du serveur (runtime).
 */
export async function environnementServeur() {
  try {
    const res = await fetch('/sante');
    const j = await res.json();
    return j?.environnement ?? null;
  } catch {
    return null;
  }
}

/**
 * Identité du technicien. En production, remplacer par le jeton du SSO Etna :
 * l'identité de l'auteur de la fiche fait partie du faisceau de preuves.
 */
const CLE_OPERATEUR = 'etna.utilisateur';

/**
 * Identité de l'opérateur, transmise à chaque requête et journalisée côté serveur.
 *
 * INTERMÉDIAIRE — à remplacer par le SSO Etna (Entra ID) avant production. C'est une
 * IDENTIFICATION, pas une AUTHENTIFICATION : l'application ne vérifie pas que la
 * personne est bien celle qu'elle déclare. Suffisant pour un test contrôlé sur le
 * réseau interne, où l'enjeu est que le journal porte un nom exploitable, pas de
 * repousser un usurpateur. Le serveur, lui, refuse toute requête sans identité.
 */
export const operateur = {
  courant: () => sessionStorage.getItem(CLE_OPERATEUR) || null,
  definir: (valeur) => sessionStorage.setItem(CLE_OPERATEUR, valeur.trim()),
  oublier: () => sessionStorage.removeItem(CLE_OPERATEUR),
};

function entetes() {
  return {
    'Content-Type': 'application/json',
    'X-Etna-Utilisateur': operateur.courant() ?? '',
  };
}

async function appeler(chemin, options = {}) {
  const res = await fetch(`${BASE}${chemin}`, { headers: entetes(), ...options });
  const corps = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(corps.erreur ?? `Erreur ${res.status}`);
    err.erreurs = corps.erreurs;
    err.status = res.status;
    throw err;
  }
  return corps;
}

export const api = {
  creerFiche: (fiche, affaireNo) =>
    appeler('/fiches', { method: 'POST', body: JSON.stringify({ fiche, affaireNo }) }),
  lancerSignature: (id, mode = 'presentiel') =>
    appeler(`/fiches/${id}/signature`, { method: 'POST', body: JSON.stringify({ mode }) }),
  /**
   * État courant des signataires, avec un lien frais pour celui dont c'est le tour.
   * Appelé à chaque étape du parcours plutôt qu'une fois à l'activation : en flux
   * ordonné, le lien du second signataire n'existe pas avant que le premier ait signé.
   */
  liensSignature: (id, phase = 'reception') =>
    appeler(`/fiches/${id}/signature/liens?phase=${phase}`),
  /** File de travail du conducteur de travaux : réceptions avec réserves non levées. */
  listerAttenteLevee: () => appeler('/fiches/attente-levee'),
  lancerLevee: (id, mode = 'presentiel') =>
    appeler(`/fiches/${id}/levee`, { method: 'POST', body: JSON.stringify({ mode }) }),
  signaturesEnAttente: (matricule) =>
    appeler(`/fiches/signatures-en-attente?matricule=${encodeURIComponent(matricule)}`),
  relancerSignataire: (demandeId, signerId) =>
    appeler(`/fiches/signature/${demandeId}/signers/${signerId}/relance`, { method: 'POST' }),
  annulerDemande: (demandeId) =>
    appeler(`/fiches/signature/${demandeId}/annuler`, { method: 'POST' }),
  lireFiche: (id) => appeler(`/fiches/${id}`),
};

/* ------------------------------------------------------------------ */
/* Persistance locale                                                  */
/* ------------------------------------------------------------------ */

/**
 * Une réception dure plusieurs minutes, sur un téléphone, en extérieur. Un appel
 * entrant, un verrouillage d'écran, un onglet purgé par le système : la saisie
 * disparaît et le technicien recommence devant le client. On persiste donc à
 * chaque frappe.
 *
 * Ce stockage contient des données personnelles (nom, e-mail, mobile du client) :
 * il est effacé dès que la fiche est signée, et il ne survit pas à une réception
 * terminée.
 */
const CLE_BROUILLON = 'etna.reception.brouillon';
const CLE_SESSION = 'etna.reception.encours';

function lire(cle) {
  try {
    const brut = localStorage.getItem(cle);
    return brut ? JSON.parse(brut) : null;
  } catch {
    return null;
  }
}

function ecrire(cle, valeur) {
  try {
    localStorage.setItem(cle, JSON.stringify(valeur));
  } catch {
    // Navigation privée ou quota atteint : la persistance est un confort, pas une
    // dépendance. On continue sans elle plutôt que d'interrompre une réception.
  }
}

function effacer(cle) {
  try {
    localStorage.removeItem(cle);
  } catch { /* voir ci-dessus */ }
}

export const brouillon = {
  charger: () => lire(CLE_BROUILLON),
  enregistrer: (fiche, etape) => ecrire(CLE_BROUILLON, { fiche, etape, le: Date.now() }),
  effacer: () => effacer(CLE_BROUILLON),
};

/**
 * Trace de la fiche envoyée en signature. C'est elle qui permet de reprendre après
 * un incident : sans elle, le technicien ne saurait pas qu'une demande est ouverte
 * et en créerait une seconde pour la même réception.
 */
export const sessionSignature = {
  charger: () => lire(CLE_SESSION),
  enregistrer: (infos) => ecrire(CLE_SESSION, { ...infos, le: Date.now() }),
  effacer: () => effacer(CLE_SESSION),
};

/* ------------------------------------------------------------------ */
/* Validation côté client                                              */
/* ------------------------------------------------------------------ */

/**
 * Miroir volontairement partiel des règles de src/domain/schema.js.
 *
 * Son rôle n'est PAS la sécurité — le serveur revalide tout — mais l'ergonomie :
 * empêcher d'atteindre l'écran de signature avec un champ vide, alors que le client
 * attend à côté. Le serveur reste seul juge ; toute divergence se règle en corrigeant
 * ce fichier, jamais en assouplissant le serveur.
 */

const TELEPHONE = /^\+[1-9]\d{7,14}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const DATE_FR = /^\d{2}\/\d{2}\/\d{4}$/;

/**
 * Normalise un numéro saisi sur le terrain vers le format international E.164.
 * Tolère : espaces/points/tirets, préfixe 00, numéro français local (0X…) et
 * l'erreur « +33 0X… ». Miroir de normaliserTelephone côté serveur.
 */
export function normaliserTelephone(brut) {
  if (typeof brut !== 'string') return brut ?? '';
  let t = brut.replace(/[\s.\-()]/g, '');
  if (t.startsWith('00')) t = `+${t.slice(2)}`;
  if (/^0\d{9}$/.test(t)) t = `+33${t.slice(1)}`;
  t = t.replace(/^\+330(\d)/, '+33$1');
  return t;
}

/** Un numéro est valide si, une fois normalisé, il respecte le format international. */
const telephoneValide = (v) => TELEPHONE.test(normaliserTelephone(v ?? ''));

export const TYPES_AVEC_SECOURS = ['elevateurPmr', 'ascenseurMaison'];

/**
 * Capacités du gabarit papier, en caractères. Doivent rester identiques aux
 * constantes du même nom dans server/src/domain/schema.js — le serveur reste
 * l'autorité, celles-ci ne servent qu'à afficher la place restante au technicien
 * pendant la saisie, plutôt que de le laisser découvrir le refus au moment de
 * faire signer.
 */
export const CAPACITE_ADRESSE = 190;
export const CAPACITE_RESERVES = 320;

export const TYPES_MATERIEL = [
  ['montePlats', 'Monte-plats'],
  ['monteChariots', 'Monte-chariots'],
  ['monteFuts', 'Monte-charge à trappe'],
  ['elevateurPmr', 'Élévateur PMR'],
  ['ascenseurMaison', 'Ascenseur de maison'],
  ['optima', 'Optima'],
  ['autre', 'Autre (préciser)'],
];

export const QUALITES = [
  ['maitreOeuvre', "Maître d'œuvre mandaté"],
  ['maitreOuvrage', "Maître d'ouvrage"],
  ['proprietaire', 'Propriétaire'],
  ['representant', 'Représentant mandaté'],
];

/** @returns {Record<string, string>} erreurs par champ, vide si l'étape est complète. */
export function validerEtape(numero, f) {
  const e = {};
  const requis = (cle, valeur, message) => {
    if (!valeur || !String(valeur).trim()) e[cle] = message;
  };

  if (numero === 0) {
    if ((f.adresse ?? '').length > CAPACITE_ADRESSE) {
      e.adresse = `Adresse trop longue pour la fiche : ${CAPACITE_ADRESSE} caractères maximum`;
    }
    if (!DATE_FR.test(f.dateReception ?? '')) e.dateReception = 'Date attendue au format JJ/MM/AAAA';
    requis('appareilNo', f.appareilNo, "Le n° d'appareil est obligatoire");
    requis('nomAffaire', f.nomAffaire, "Le nom de l'affaire est obligatoire");
    requis('adresse', f.adresse, "L'adresse du chantier est obligatoire");
  }

  if (numero === 1) {
    if (!f.materiel.types.length) e.types = 'Cocher au moins un type de matériel';
    if (f.materiel.types.includes('autre')) {
      requis('autrePrecision', f.materiel.autrePrecision, 'Préciser le type de matériel');
    }
    requis('designation', f.materiel.designation, 'Le type est obligatoire');
    requis('nbreNiveaux', f.materiel.nbreNiveaux, 'Le nombre de niveaux est obligatoire');
    requis('chargeKg', f.materiel.chargeKg, 'La charge est obligatoire');
    if (f.materiel.types.some((t) => TYPES_AVEC_SECOURS.includes(t)) && f.secoursTelephoniqueVerifie !== true) {
      e.secoursTelephoniqueVerifie =
        'Confirmer que le dispositif de secours par téléphone est en état de fonctionnement';
    }
  }

  if (numero === 2) {
    if (f.avecReserves === null || f.avecReserves === undefined) {
      e.avecReserves = 'Préciser si la réception est prononcée avec ou sans réserve';
    } else if (f.avecReserves) {
      requis('natureReserves', f.natureReserves, 'Décrire la nature des réserves à lever');
      if ((f.natureReserves ?? '').length > CAPACITE_RESERVES) {
        e.natureReserves = `Trop long pour les trois lignes de la fiche : ${CAPACITE_RESERVES} caractères maximum. Prévoir une annexe.`;
      }
    }
  }

  if (numero === 3) {
    requis('clientNom', f.signataireClient.nomComplet, 'Le nom du signataire est obligatoire');
    if (!f.signataireClient.qualite) e.clientQualite = 'Préciser la qualité du signataire';
    if (
      ['maitreOeuvre', 'representant'].includes(f.signataireClient.qualite)
      && f.signataireClient.habilitationMoa !== true
    ) {
      e.clientHabilitation = "Attester être mandaté par le maître d'ouvrage";
    }
    if (!EMAIL.test(f.signataireClient.email ?? '')) e.clientEmail = 'Adresse e-mail invalide';
    if (!telephoneValide(f.signataireClient.telephone)) {
      e.clientTelephone = 'Numéro invalide, ex. 06 12 34 56 78 ou +33 6 12 34 56 78';
    }
    requis('etnaNom', f.representantEtna.nomComplet, 'Le nom du représentant est obligatoire');
    if (!EMAIL.test(f.representantEtna.email ?? '')) e.etnaEmail = 'Adresse e-mail invalide';
    if (!telephoneValide(f.representantEtna.telephone)) {
      e.etnaTelephone = 'Numéro invalide, ex. 06 12 34 56 78 ou +33 6 12 34 56 78';
    }
    if (f.representantEtna.habilite !== true) {
      e.etnaHabilite = 'Confirmer que vous êtes mandaté pour prononcer la réception';
    }
    if (
      f.signataireClient.email && f.representantEtna.email
      && f.signataireClient.email.toLowerCase() === f.representantEtna.email.toLowerCase()
    ) {
      e.clientEmail = 'Le client et le représentant Etna doivent avoir des adresses distinctes';
    }
  }

  return e;
}

// Adresse partagée des conducteurs travaux, pré-remplie par défaut (modifiable au besoin).
export const EMAIL_TRAVAUX_PARDEFAUT = 'travaux@etnafrance.com';

export function ficheVide() {
  return {
    dateReception: new Date().toLocaleDateString('fr-FR'),
    appareilNo: '',
    devisNo: '',
    nomAffaire: '',
    adresse: '',
    materiel: { types: [], autrePrecision: '', designation: '', nbreNiveaux: '', chargeKg: '' },
    secoursTelephoniqueVerifie: false,
    avecReserves: null,
    natureReserves: '',
    signataireClient: { nomComplet: '', qualite: '', email: '', telephone: '' },
    representantEtna: { nomComplet: '', email: EMAIL_TRAVAUX_PARDEFAUT, telephone: '', habilite: false },
  };
}

/** Nettoie la fiche avant envoi : le serveur refuse les champs incohérents. */
export function pourEnvoi(f) {
  const fiche = structuredClone(f);
  if (!fiche.avecReserves) delete fiche.natureReserves;
  if (!fiche.materiel.types.includes('autre')) delete fiche.materiel.autrePrecision;
  if (!fiche.materiel.types.some((t) => TYPES_AVEC_SECOURS.includes(t))) {
    delete fiche.secoursTelephoniqueVerifie;
  }
  // Envoi en format international canonique (le serveur et Youtrust l'exigent).
  fiche.signataireClient.telephone = normaliserTelephone(fiche.signataireClient.telephone);
  fiche.representantEtna.telephone = normaliserTelephone(fiche.representantEtna.telephone);
  return fiche;
}
